/**
 * contactSyncStorage.ts
 *
 * IndexedDB persistence layer for the E2EE contact sync feature.
 *
 * Two object stores:
 *   session  — { deviceId, privateKey (CryptoKey), publicKeyBase64, linkedAt }
 *   contacts — the last decrypted contact array as a JSON-serialisable value
 *
 * CryptoKey objects are stored directly via the structured-clone algorithm
 * (supported by all modern browsers). This avoids ever exporting raw key bytes
 * into JavaScript — the private key material stays inside the browser's
 * key management subsystem.
 */

import { openDB, type IDBPDatabase } from 'idb';
import type { SyncedContact } from '~/types/contacts';

const DB_NAME = 'beesphone-contacts-sync';
// Bump version whenever the StoredSession schema changes so that existing
// sessions with an incompatible shape are cleared rather than misread.
// v1 → v2: added required `uid` field to StoredSession.
const DB_VERSION = 2;
const SESSION_STORE = 'session';
const CONTACTS_STORE = 'contacts';

const SESSION_KEY = 'current';
const CONTACTS_KEY = 'all';

export interface StoredSession {
  deviceId: string;
  privateKey: CryptoKey;
  publicKeyBase64: string;
  // null when the session was generated for a QR code but the mobile has not
  // yet scanned it. Set to an ISO-8601 string on the first successful sync.
  linkedAt: string | null;
  // Firebase UID that owns this session. Used by initialize() to detect when a
  // different user logs in on the same browser and avoid cross-user data leaks.
  uid: string;
}

type ContactSyncDB = {
  [SESSION_STORE]: {
    key: string;
    value: StoredSession;
  };
  [CONTACTS_STORE]: {
    key: string;
    value: SyncedContact[];
  };
};

// Lazily-opened DB instance (one per page lifetime).
let _db: IDBPDatabase<ContactSyncDB> | null = null;

async function getDb(): Promise<IDBPDatabase<ContactSyncDB>> {
  if (_db) return _db;
  _db = await openDB<ContactSyncDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, _newVersion, transaction) {
      // v1: initial schema — create both object stores.
      if (oldVersion < 1) {
        db.createObjectStore(SESSION_STORE);
        db.createObjectStore(CONTACTS_STORE);
      }
      // v1 → v2: `uid` became a required field on StoredSession.
      // Clear any stored session that pre-dates this change to prevent
      // cross-user data from being used under a different account.
      // SESSION_STORE always exists here: created above (oldVersion = 0)
      // or inherited from the v1 database.
      if (oldVersion < 2) {
        transaction.objectStore(SESSION_STORE).clear();
      }
    },
  });
  return _db;
}

// ── Session (keypair + device ID) ─────────────────────────────────────────────

export async function readSession(): Promise<StoredSession | undefined> {
  const db = await getDb();
  return db.get(SESSION_STORE, SESSION_KEY);
}

export async function writeSession(session: StoredSession): Promise<void> {
  const db = await getDb();
  await db.put(SESSION_STORE, session, SESSION_KEY);
}

export async function clearSession(): Promise<void> {
  const db = await getDb();
  await db.delete(SESSION_STORE, SESSION_KEY);
}

// ── Decrypted Contacts Cache ──────────────────────────────────────────────────

export async function readCachedContacts(): Promise<SyncedContact[]> {
  const db = await getDb();
  return (await db.get(CONTACTS_STORE, CONTACTS_KEY)) ?? [];
}

export async function writeCachedContacts(contacts: SyncedContact[]): Promise<void> {
  const db = await getDb();
  await db.put(CONTACTS_STORE, contacts, CONTACTS_KEY);
}

export async function clearCachedContacts(): Promise<void> {
  const db = await getDb();
  await db.delete(CONTACTS_STORE, CONTACTS_KEY);
}

// ── Full Wipe ─────────────────────────────────────────────────────────────────

/** Removes all sync data. Call on session key loss or when the user unlinks. */
export async function clearAll(): Promise<void> {
  await Promise.all([clearSession(), clearCachedContacts()]);
}
